import { useEffect, useMemo, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useAppearance } from './AppearanceProvider';
import { landscapeValues, projectLandscape } from './experience-landscape';
import EquitySignal from './EquitySignal';

export default function PerformanceLandscape({ points, quiet = false }) {
  const canvas = useRef(null), reduced = useReducedMotion();
  const { theme, preferences } = useAppearance();
  const values = useMemo(() => landscapeValues(points), [points]);
  useEffect(() => {
    const node = canvas.current, parent = node?.parentElement;
    if (!node || !parent) return;
    const ctx = node.getContext('2d', { alpha: true });
    if (!ctx) return;
    let frame, width = 0, height = 0, visible = true, running = false, last = 0;
    const aim = { x: 0, y: 0 }, camera = { x: 0, y: 0 };
    const still = reduced || quiet || preferences.quiet;
    const draw = time => {
      ctx.clearRect(0, 0, width, height);
      camera.x += (aim.x - camera.x) * .055; camera.y += (aim.y - camera.y) * .055;
      const yaw = -.34 + camera.x * .17 + (still ? 0 : Math.sin(time / 9000) * .045);
      const float = still ? 0 : Math.sin(time / 3100) * .025;
      const project = (x, y, z) => projectLandscape(x, y + float + camera.y * .035, z, yaw, width, height);
      // The wire surface is painted into a single modest-resolution canvas, not
      // dozens of animated DOM layers or a WebGL dependency.
      for (let row = 35; row >= 0; row--) {
        const z = (row / 35 - .5) * 1.58;
        const edge = Math.sin(row / 35 * Math.PI);
        ctx.beginPath();
        values.forEach((value, index) => {
          const along = index / (values.length - 1), x = (along - .5) * 3;
          const curl = Math.sin(along * Math.PI * 1.25 + row / 35 * .9);
          const y = (value - .35) * .95 + edge * .2 + curl * .08;
          const [px, py] = project(x, y, z);
          if (!index) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        });
        ctx.strokeStyle = theme.accent;
        ctx.globalAlpha = theme.light ? .13 + edge * .23 : .13 + edge * .44;
        ctx.lineWidth = row === 18 ? 1.8 : .85; ctx.stroke();
      }
      // Cross lines reveal the shape as the camera moves.
      for (let column = 0; column < values.length; column += 4) {
        ctx.beginPath();
        for (let row = 0; row <= 35; row++) {
          const along = column / (values.length - 1), z = (row / 35 - .5) * 1.58;
          const y = (values[column] - .35) * .95 + Math.sin(row / 35 * Math.PI) * .2 + Math.sin(along * Math.PI * 1.25 + row / 35 * .9) * .08;
          const [px, py] = project((along - .5) * 3, y, z);
          if (!row) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.globalAlpha = .13; ctx.lineWidth = .65; ctx.stroke();
      }
      // A moving pin traces the sculpture. Its movement is atmosphere only.
      if (values.length > 1) {
        const position = still ? values.length - 1 : time / 110 % (values.length - 1);
        const index = Math.floor(position), next = Math.min(values.length - 1,index + 1);
        const value = values[index] + (values[next] - values[index]) * (position - index);
        const along = position / (values.length - 1);
        const [x, y] = project((along - .5) * 3, (value - .35) * .95 + .2 + Math.sin(along * Math.PI * 1.25 + .5) * .08, 0);
        const halo = ctx.createRadialGradient(x, y, 0, x, y, 24);
        halo.addColorStop(0, `${theme.accent}70`); halo.addColorStop(1, `${theme.accent}00`);
        ctx.globalAlpha = 1; ctx.fillStyle = halo; ctx.fillRect(x - 24, y - 24, 48, 48);
        ctx.fillStyle = theme.light ? theme.accent : '#edfff3'; ctx.beginPath(); ctx.arc(x, y, 2.4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    const tick = time => {
      if (!running) return;
      if (time - last >= (window.innerWidth >= 700 ? 16 : 32)) { draw(time); last = time; }
      frame = requestAnimationFrame(tick);
    };
    const update = () => {
      const next = visible && !document.hidden && !still;
      if (next && !running) { running = true; frame = requestAnimationFrame(tick); }
      else if (!next) { running = false; cancelAnimationFrame(frame); if (visible && !document.hidden) draw(0); }
    };
    const resize = () => {
      width = parent.clientWidth; height = parent.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      node.width = Math.round(width * ratio); node.height = Math.round(height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0); draw(0);
    };
    const move = event => {
      if (event.pointerType === 'touch') return;
      const rect = parent.getBoundingClientRect();
      aim.x = (event.clientX - rect.left) / rect.width * 2 - 1;
      aim.y = (event.clientY - rect.top) / rect.height * 2 - 1;
    };
    const leave = () => { aim.x = 0; aim.y = 0; };
    const sizeObserver = new ResizeObserver(resize); sizeObserver.observe(parent);
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => { visible = entries[0].isIntersecting; update(); }) : null;
    observer?.observe(node); parent.addEventListener('pointermove', move); parent.addEventListener('pointerleave', leave);
    document.addEventListener('visibilitychange', update); resize(); update();
    return () => { running = false; cancelAnimationFrame(frame); sizeObserver.disconnect(); observer?.disconnect(); parent.removeEventListener('pointermove', move); parent.removeEventListener('pointerleave', leave); document.removeEventListener('visibilitychange', update); };
  }, [values, theme, reduced, quiet, preferences.quiet]);
  return <><canvas ref={canvas} className="tl-landscape-canvas" aria-hidden="true"/><EquitySignal points={points} quiet={quiet}/></>;
}
